import type { CSSProperties } from 'react';

import type { TextCalloutTextSize, ThemeTokens } from '@course-authoring/shared-types';

export interface TextCalloutStyles {
  card: CSSProperties;
  content: CSSProperties;
}

// NA-205: callouts step down again on narrow phones (≤576px); line-height is "normal",
// so it tracks the smaller font automatically.
const TEXT_SIZE_PX: Record<TextCalloutTextSize, { desktop: string; mobile: string; smallMobile: string }> = {
  smaller: { desktop: '30px', mobile: '24px', smallMobile: '20px' },
  standard: { desktop: '40px', mobile: '32px', smallMobile: '26px' },
  larger: { desktop: '50px', mobile: '40px', smallMobile: '32px' },
};

/**
 * Component-layout values for the Text Callout card. Inlined as part of the
 * Phase 4 component-token flatten — these are bespoke layout values (widths,
 * paddings, white card border) not part of the design-system token set.
 * Color / shadow values come from the design-system primitives.
 */
const TEXT_CALLOUT = {
  width: { desktop: '80%', mobile: '90%' },
  // NA-205: callout box padding tightens to a flat 20px (lgPlus) on narrow phones (≤576px).
  padding: { desktop: '32px 64px 32px 64px', mobile: '24px 24px 24px 24px', smallMobile: '20px' },
  gap: '10px',
  borderWidth: '3px',
} as const;

export function createTextCalloutStyles(
  tokens: ThemeTokens,
  isMobile: boolean,
  isSmallMobile: boolean,
  textSize: TextCalloutTextSize = 'standard',
): TextCalloutStyles {
  const sizeEntry = TEXT_SIZE_PX[textSize];
  const fontSize = isSmallMobile
    ? sizeEntry.smallMobile
    : isMobile
      ? sizeEntry.mobile
      : sizeEntry.desktop;
  const padding = isSmallMobile
    ? TEXT_CALLOUT.padding.smallMobile
    : isMobile
      ? TEXT_CALLOUT.padding.mobile
      : TEXT_CALLOUT.padding.desktop;

  return {
    card: {
      width: isMobile ? TEXT_CALLOUT.width.mobile : TEXT_CALLOUT.width.desktop,
      maxWidth: '100%',
      backgroundColor: tokens.backgrounds.cardAlt,
      borderRadius: tokens.radius.xl,
      padding,
      border: `${TEXT_CALLOUT.borderWidth} solid ${tokens.backgrounds.app}`,
      boxShadow: tokens.shadow.lg,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: TEXT_CALLOUT.gap,
      flexShrink: 0,
      alignSelf: 'center',
      margin: '0 auto',
    },
    content: {
      width: '100%',
      color: tokens.text.callout,
      fontFamily: tokens.typography.fontFamily,
      fontSize,
      fontStyle: 'normal',
      fontWeight: isMobile
        ? tokens.typography.mobile.callout.weight
        : tokens.typography.callout.weight,
      lineHeight: isMobile
        ? tokens.typography.mobile.callout.lineHeight
        : tokens.typography.callout.lineHeight,
      textAlign: 'left',
    },
  };
}
